import { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import {
  CheckErrorType,
  LoginTypes,
  ParamsType,
  RegisterFieldsType,
  StatesType,
  RegErrorType,
  AboutType,
} from '../interface';
import { ExamcompassClient } from '../Api/api';
import { DecryptedAuthToken, encrypt } from '../utils/encryption';
import { useNavigate } from 'react-router';
import axios from 'axios';

export interface ExamCompassContextType {
  successMessage: string | null;
  states: StatesType[] | null;
  registerFields: RegisterFieldsType;
  handleRegistrationInput: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  registerUser: (e: React.FormEvent) => Promise<void>;
  error: CheckErrorType | null;
  regNonFieldError: string;
  fieldErrors: RegErrorType | null;
  registrationSuccessful: boolean;
  handleClose: () => void;
  loginInput: LoginTypes;
  handleLoginInputs: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  userRef: React.MutableRefObject<HTMLInputElement | null>;
  loginError: string | null;
  setLoginError: React.Dispatch<React.SetStateAction<string | null>>;
  loginNonFieldErr: string | null;
  loginUser: (e: React.FormEvent) => Promise<void>;
  loading: boolean;
  about: AboutType | null;
  toDashboard: () => void;
  logoutUser: () => void;
}

export const ExamCompassContext = createContext<
  Partial<ExamCompassContextType>
>({});

interface ExamCompassContextProviderProps {
  children: ReactNode;
}

export const ExamCompassContextProvider: React.FC<
  ExamCompassContextProviderProps
> = ({ children }) => {
  const [about, setAbout] = useState<AboutType | null>(null);

  useEffect(() => {
    const fetchAboutContents = async () => {
      try {
        const response = await ExamcompassClient.get('/info/about_list/');
        setAbout(response?.data);
      } catch (err) {
        console.log('Error fetching about content', err);
      }
    };

    fetchAboutContents();
  }, []);

  // _______REGISTER_______
  // For List of states
  const [states, setStates] = useState<StatesType[] | null>(null);
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await ExamcompassClient.get('/info/state_list');
        setStates(response.data);
      } catch (error) {
        console.log('Something went wrong', error);
      }
    };

    fetchStates();
  }, []);

  const [params, setParams] = useState<ParamsType>({
    ref: '',
    refCode: '',
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const ref = searchParams.get('ref') || localStorage.getItem('ref') || '';
    const refCode = searchParams.get('ref_code') || '';

    if (ref) {
      localStorage.setItem('ref', ref); // Save ref in localStorage
    }

    setParams({ ref, refCode });
  }, []);

  const [registerFields, setRegisterFields] = useState<RegisterFieldsType>({
    userName: '',
    email: '',
    phoneNumber: '',
    state: '',
    receiveEmail: false,
    referralCode: '',
    password: '',
    confirmPassword: '',
    termAccepted: false,
  });

  // Update `registerFields.referralCode` when `params.ref` changes
  useEffect(() => {
    setRegisterFields((prevFields) => ({
      ...prevFields,
      referralCode: params.refCode,
    }));
  }, [params.refCode]);

  // Check for successful registration
  const [registrationSuccessful, setRegistrationSuccessful] =
    useState<boolean>(false);

  // Submit login details
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    registrationSuccessful && setRegistrationSuccessful(false);
  };

  // For registration form error
  const [error, setError] = useState<CheckErrorType>({
    confirmPassword: '',
    termAccepted: '',
  });
  const [fieldErrors, setFieldErrors] = useState<RegErrorType | null>(null);
  const [regNonFieldError, setRegNonFieldError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleRegistrationInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    // Destructure name and value
    const { name, value, type } = e.target;
    if (type === 'checkbox' && e.target instanceof HTMLInputElement) {
      const { checked } = e.target;
      setRegisterFields((prev) => ({ ...prev, [name]: checked }));
    } else {
      setRegisterFields((prev) => ({ ...prev, [name]: value }));
    }
  };

  const navigate = useNavigate();

  // Function to registerUser
  const registerUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!registerFields.confirmPassword) {
      setError((prev) => ({
        ...prev,
        confirmPassword: 'This field may not be blank.',
      }));
      return;
    } else if (registerFields.confirmPassword !== registerFields.password) {
      setError((prev) => ({
        ...prev,
        confirmPassword: 'Passwords do not match.',
      }));
      return;
    }
    if (registerFields.termAccepted === false) {
      setError((prev) => ({
        ...prev,
        termAccepted: 'Accept terms and condition',
      }));
      return;
    }

    const newUser = {
      username: registerFields.userName,
      password: registerFields.password,
      email: registerFields.email,
      profile: {
        phone_number: `+234${registerFields?.phoneNumber}`,
        receive_mails: registerFields?.receiveEmail === true ? true : false,
        state: registerFields?.state,
        ...(registerFields.referralCode && {
          ref_code: registerFields.referralCode,
        }),
        ...(params.ref && { referral_code: params.ref }),
      },
    };

    const {
      email,
      password,
      username,
      profile: { phone_number, state, receive_mails },
    } = newUser;
    if (!email || !password || !phone_number || !username || !state || !receive_mails) {
      setloginError('Please provide all values. And tick the checkboxes');
      return;
    }

    try {
      setLoading(true);

      const response = await ExamcompassClient.post(
        '/profile/create_user/',
        newUser
      );
      setRegisterFields({
        userName: '',
        email: '',
        phoneNumber: '',
        state: '',
        receiveEmail: false,
        referralCode: '',
        password: '',
        confirmPassword: '',
        termAccepted: false,
      });
      if (response.status === 201) {
        setLoading(false);
        localStorage.removeItem('ref');
        navigate('/email-verification');
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const regError = error?.response?.data;
        console.log('reg_error>>>>>>>>>>>', regError);
        setFieldErrors(regError);
        console.log('fillee_error>>>>>>>>>>>', fieldErrors)
        if (
          regError &&
          typeof regError === 'object' &&
          !regError?.bank_data?.[0]?.non_field_errors
        ) {
          setFieldErrors(regError);
        } else if (
          regError &&
          typeof regError === 'object' &&
          regError?.non_field_errors
        ) {
          setRegNonFieldError(regError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // _______LOGIN_______
  // For login inputs
  const [loginInput, setLoginInput] = useState<LoginTypes>({
    username: '',
    password: '',
    rememberMe: false,
  });
  const [loginError, setloginError] = useState<string | null>(null);
  const [loginNonFieldErr, setLoginNonFieldErr] = useState<string | null>('');

  const userRef = useRef<HTMLInputElement | null>(null);

  const handleLoginInputs = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox' && e.target instanceof HTMLInputElement) {
      const { checked } = e.target;
      setLoginInput((prev) => ({ ...prev, [name]: checked }));
    } else {
      setLoginInput((prev) => ({ ...prev, [name]: value }));
    }
  };

  useEffect(() => {
    userRef?.current?.focus();
  }, []);

  const loginUser = async (e: React.FormEvent) => {
    const dashboardUrl = process.env.REACT_APP_DASHBOARD_URL;
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    const loginDetails = {
      username: loginInput.username,
      password: loginInput.password,
    };

    try {
      const response = await ExamcompassClient.post(
        '/profile/token-auth/',
        loginDetails
      );
      console.log('response_from_login>>>>', response);

      const authToken: string | null = response.data?.token;
      if (authToken !== null) {
        setSuccessMessage('Login Successful, Redirecting!');
        setLoginNonFieldErr(null);
        setFieldErrors(null);
        if (loginInput.rememberMe) {
          localStorage.setItem('authToken', authToken);
        } else {
          sessionStorage.setItem('authToken', authToken);
        }

        const payload: DecryptedAuthToken = {
          authToken: authToken,
          rememberMe: loginInput.rememberMe,
        };
        const token = encrypt(payload, 'salt');
        const encodedParam = btoa(token);

        const redirectURL = `${dashboardUrl}/auth/${encodedParam}`;
        window.location.href = redirectURL;

        setLoginInput({ username: '', password: '', rememberMe: false });
      }
    } catch (error: any) {
      console.log('error_here>>>>>>>>>>>', error);
      setloginError(
        error?.response?.data?.[0] ||
          error?.response?.data?.non_field_errors?.[0]
      );
      if (axios.isAxiosError(error)) {
        const loginError =
          error?.response?.data?.non_field_errors || error?.response?.data;
        setLoginNonFieldErr(loginError[0]);
        console.log('LoginError>>>>', error);

        // if (loginError && loginError?.non_field_errors) {
        //   setLoginNonFieldErr(loginError?.non_field_errors[0]);
        // }
      }
      console.log('errr_login_in>>>>', error);
    } finally {
      setLoading(false);
    }
  };

  // ____BACK TO DASHBOARD IF USER IS LOGGED IN____
  const toDashboard = () => {
    const dashboardUrl = process.env.REACT_APP_DASHBOARD_URL;

    if (!dashboardUrl) {
      console.error('Dashboard URL is not configured.');
      return;
    }

    // Check for token in localStorage or sessionStorage
    const authToken =
      localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!authToken) {
      console.error('Authentication token not found.');
      return;
    }

    // Create the encrypted payload
    const payload: DecryptedAuthToken = {
      authToken,
      rememberMe: !!localStorage.getItem('authToken'), // Determine if the token was stored in localStorage
    };

    const token = encrypt(payload, 'salt');
    const encodedParam = btoa(token);

    // Redirect to the dashboard
    const redirectURL = `${dashboardUrl}/auth/${encodedParam}`;
    window.location.href = redirectURL;
  };

  // ----------------LOGOUT-----------------/
  const logoutUser = () => {
    // Clear tokens from storage
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');

    // Replace with your desired route
    window.location.href = '/';
  };

  const contextValue: ExamCompassContextType = {
    states,
    successMessage,
    registerFields,
    handleRegistrationInput,
    registerUser,
    registrationSuccessful,
    error,
    regNonFieldError,
    fieldErrors,
    handleClose,
    loginInput,
    handleLoginInputs,
    userRef,
    loginError,
    setLoginError: setloginError,
    loginNonFieldErr,
    loginUser,
    loading,
    about,
    toDashboard,
    logoutUser,
  };
  return (
    <ExamCompassContext.Provider value={contextValue}>
      {children}
    </ExamCompassContext.Provider>
  );
};
